import { useEffect, useState } from 'react';
import { Button, Modal, Popconfirm, Progress, Spin } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { BalloonGenerationJob } from '../../interfaces/Ballooning';
import { formatElapsed } from '../../utils/formatElapsed';

interface GenerationProgressModalProps {
	open: boolean;
	job: BalloonGenerationJob | null | undefined;
	cancelling: boolean;
	onCancelGeneration: () => void;
}

/** Seconds since the job started, or since it was queued; ticks every second. */
const useElapsedSeconds = (
	job: BalloonGenerationJob | null | undefined,
	active: boolean
) => {
	const since = job?.started_at || job?.created_time;
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (!active) return;
		setNow(Date.now());
		const timer = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(timer);
	}, [active]);
	return since ? (now - new Date(since).getTime()) / 1000 : 0;
};

/** Blocks the editor while the AI analyzes the drawing; the run can be stopped from here. */
export default function GenerationProgressModal({
	open,
	job,
	cancelling,
	onCancelGeneration,
}: GenerationProgressModalProps) {
	const running = job?.status === 'running';
	const elapsed = useElapsedSeconds(job, open);
	const pagesTotal = job?.pages_total ?? 0;
	const stage = running ? job?.stage : '';
	const step = stage?.match(/\((\d+) of (\d+)\)$/);
	const stepPercent = step
		? Math.round((Number(step[1]) / Number(step[2])) * 100)
		: null;

	return (
		<Modal
			open={open}
			centered
			closable={false}
			maskClosable={false}
			keyboard={false}
			width={380}
			data-testid="generation-progress-modal"
			footer={
				<Popconfirm
					title="Stop the analysis?"
					description="Nothing from this run is saved. You can start it again."
					okText="Stop analysis"
					okButtonProps={{ danger: true }}
					cancelText="Keep going"
					onConfirm={onCancelGeneration}
				>
					<Button
						block
						icon={<StopOutlined />}
						loading={cancelling}
						data-testid="cancel-generation-button"
					>
						Cancel
					</Button>
				</Popconfirm>
			}
		>
			<div className="flex flex-col items-center gap-3 pt-6 text-center">
				<Spin size="large" />
				<div className="text-base font-semibold">
					Generating inspection plan
				</div>
				<div
					className="text-sm tabular-nums text-gray-500"
					aria-label="Time analyzing"
					data-testid="generation-elapsed"
				>
					{formatElapsed(elapsed)}
				</div>
				{running && pagesTotal > 1 && (
					<div className="text-xs uppercase tracking-wide text-gray-400">
						Page {Math.min((job?.pages_done ?? 0) + 1, pagesTotal)} of{' '}
						{pagesTotal}
					</div>
				)}
				<div className="text-gray-600" data-testid="generation-stage">
					{stage
						? `${stage}…`
						: running
						? 'Starting…'
						: 'Queued, waiting for the analyzer to start…'}
				</div>
				{stepPercent !== null && (
					<Progress
						percent={stepPercent}
						size="small"
						showInfo={false}
						className="!m-0 w-full px-6"
					/>
				)}
				<div className="text-xs text-gray-400">
					The drawing is being analyzed with AI. This can take a minute or two.
				</div>
			</div>
		</Modal>
	);
}
